/**
 * File upload hook with progress tracking.
 * ✅ Now supports upload cancellation via abort()
 */

import { useState, useCallback } from 'react';
import { uploadFile, UploadProgress, UploadResult, abortAllUploads } from '@/services/obsService';
import type { UploadConfig } from '@/types';

interface UseFileUploadReturn {
  isUploading: boolean;
  progress: UploadProgress | null;
  error: string | null;
  upload: (file: File) => Promise<UploadResult>;
  reset: () => void;
  abort: () => void;  // ✅ NEW: Abort all active uploads
}

export const useFileUpload = (config?: UploadConfig): UseFileUploadReturn => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(async (file: File): Promise<UploadResult> => {
    setIsUploading(true);
    setProgress({ loaded: 0, total: file.size, percent: 0 });
    setError(null);

    try {
      const result = await uploadFile(file, setProgress, config);
      setIsUploading(false);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : '上传失败';
      setError(message);
      setIsUploading(false);
      throw err;
    }
  }, [config]);

  const abort = useCallback(() => {
    // ✅ NEW: Abort all active uploads globally
    abortAllUploads();
    setIsUploading(false);
    setProgress(null);
    setError('上传已取消');
  }, []);

  const reset = useCallback(() => {
    setIsUploading(false);
    setProgress(null);
    setError(null);
  }, []);

  return {
    isUploading,
    progress,
    error,
    upload,
    reset,
    abort,  // ✅ NEW: Export abort method
  };
};

